import type { ToolPhase, WalterState } from "./types";

export class WalterAudioEngine {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  enabled = false;

  async enable() {
    if (!this.context) {
      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.master.gain.value = 0.12;
      this.master.connect(this.context.destination);
    }
    await this.context.resume();
    this.enabled = true;
    this.chime([164.81, 246.94, 329.63], 0.75, 0.055);
  }

  disable() {
    this.enabled = false;
    if (this.master && this.context) this.master.gain.setTargetAtTime(0.0001, this.context.currentTime, 0.04);
  }

  setVolume(value: number) {
    if (!this.master || !this.context) return;
    this.master.gain.setTargetAtTime(Math.max(0.0001, Math.min(0.22, value * 0.22)), this.context.currentTime, 0.03);
  }

  cueState(state: WalterState) {
    if (!this.enabled) return;
    switch (state) {
      case "aware": this.chime([196, 293.66, 392], 0.9, 0.045); break;
      case "listening": this.chime([146.83, 220], 1.2, 0.035); break;
      case "thinking": this.chime([130.81, 207.65, 311.13], 1.5, 0.028); break;
      case "speaking": this.chime([174.61, 261.63], 0.65, 0.03); break;
      case "sleeping": this.chime([220, 164.81, 110], 1.8, 0.035); break;
      default: break;
    }
  }

  cueTool(phase: ToolPhase) {
    if (!this.enabled || phase === "idle") return;
    this.chime(phase === "outgoing" ? [196, 293.66, 440, 659.25] : [659.25, 440, 293.66, 196], 1.35, 0.035);
  }

  private chime(frequencies: number[], duration: number, peak: number) {
    if (!this.context || !this.master || !this.enabled) return;
    const now = this.context.currentTime;
    frequencies.forEach((frequency, index) => {
      const oscillator = this.context!.createOscillator();
      const gain = this.context!.createGain();
      const start = now + index * 0.075;
      oscillator.type = index % 2 ? "sine" : "triangle";
      oscillator.frequency.setValueAtTime(frequency, start);
      oscillator.detune.setValueAtTime(index % 2 ? 4 : -4, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(peak, start + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
      oscillator.connect(gain);
      gain.connect(this.master!);
      oscillator.start(start);
      oscillator.stop(start + duration + 0.05);
    });
  }
}

export const walterAudio = new WalterAudioEngine();
